import React from "react";
import { Box, Flex } from "theme-ui";
import moment from "moment-timezone";

import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";

import DisclosureBlock, { Disclosure } from "./DisclosureBlock";

/**
 * MASTER_PLAN.md §3.9 #11 (mockup 23 second panel) — subsequent-view
 * banner stack. Renders after the provider has acknowledged; each
 * disclosure block renders in collapsible form so the provider can
 * dismiss the visual weight after re-reading.
 *
 * SESSION 45 audit finding GAP-42 fix. Previous version rendered a
 * bare `Terms acknowledged` line — no who, no when count. Mockup 23
 * Panel 2 specifies the audit format verbatim:
 * `Accepted by David Chen on Jul 22, 2026 at 3:14 PM · 2 of 2 notes
 * reviewed at that time`. The DB captures `termsAcknowledgedAt` /
 * `termsAcknowledgedBy` (already threaded through here); the
 * notes-reviewed count is passed in as `notesReviewedCount`, sourced
 * from the modal's snapshot of `disclosures.length` at check-time so
 * the audit reflects what was actually on-screen when the checkbox
 * ticked, not what happens to be attached now (a plan can gain or
 * lose disclosures over the PO's lifetime).
 *
 * If disclosures is empty AND the PO has an acknowledgment timestamp,
 * renders only the audit footer (no visual overhead beyond one line
 * of grey text). Empty disclosures + no acknowledgment renders
 * nothing.
 */
export default function AcceptanceBanner({
  disclosures,
  termsAcknowledgedAt,
  termsAcknowledgedBy,
  notesReviewedCount,
  accepted,
}: {
  /** PO actually accepted (status >= 2). Until then the line says the
   * TERMS were acknowledged - "Accepted by" on a Sent PO read as done
   * when it was not (2026-10-20). */
  accepted?: boolean;
  disclosures: Disclosure[];
  termsAcknowledgedAt: string | null;
  termsAcknowledgedBy: { id: string; name: string | null } | null;
  /** Optional — count of notes visible at acknowledgment time. Only
   * available on this session (post-modal); persistent audit render
   * falls back to "Accepted …" without the notes suffix. */
  notesReviewedCount?: number | null;
}) {
  if (!termsAcknowledgedAt && disclosures.length === 0) return null;

  return (
    <Box sx={{ marginBottom: "20px" }}>
      {disclosures.map((d, idx) => (
        <DisclosureBlock
          key={`${d.type}-${idx}`}
          disclosure={d}
          collapsible
          initialCollapsed={idx > 0}
        />
      ))}
      {termsAcknowledgedAt && (
        <Flex sx={{ alignItems: "center", gap: "6px", marginTop: "4px" }}>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "3px",
              background: "#27500A",
            }}
          />
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            margin={0}
            color={themevals.colors.dkgrey}
          >
            {buildAuditLine({
              termsAcknowledgedAt,
              termsAcknowledgedBy,
              notesReviewedCount,
              totalNotes: disclosures.length,
              accepted,
            })}
          </Subtext>
        </Flex>
      )}
    </Box>
  );
}

/**
 * Assembles the mockup 23 Panel 2 line. Falls back gracefully when
 * pieces are missing:
 *   - full: `Accepted by David Chen on Jul 22, 2026 at 3:14 PM · 2 of 2 notes reviewed at that time`
 *   - no name: `Accepted on Jul 22, 2026 at 3:14 PM · 2 of 2 notes reviewed at that time`
 *   - no notes-count history: `Accepted by David Chen on Jul 22, 2026 at 3:14 PM`
 *   - non-plan PO with no notes at all: `Accepted by David Chen on Jul 22, 2026 at 3:14 PM`
 */
function buildAuditLine({
  termsAcknowledgedAt,
  termsAcknowledgedBy,
  notesReviewedCount,
  totalNotes,
  accepted,
}: {
  accepted?: boolean;
  termsAcknowledgedAt: string;
  termsAcknowledgedBy: { id: string; name: string | null } | null;
  notesReviewedCount?: number | null;
  totalNotes: number;
}): string {
  const who = termsAcknowledgedBy?.name ? `by ${termsAcknowledgedBy.name} ` : "";
  const when = moment(termsAcknowledgedAt).format("MMM D, YYYY [at] h:mm A");

  // Notes suffix only renders when we know how many were reviewed AND
  // the total shown at check-time was > 0. If the count is available
  // from this session, prefer it (that's what was actually reviewed);
  // otherwise omit rather than guess.
  let notesSuffix = "";
  if (typeof notesReviewedCount === "number" && notesReviewedCount > 0) {
    const denom = Math.max(totalNotes, notesReviewedCount);
    notesSuffix = ` · ${notesReviewedCount} of ${denom} note${denom === 1 ? "" : "s"} reviewed at that time`;
  }

  return `${accepted === false ? "Terms acknowledged" : "Accepted"} ${who}on ${when}${notesSuffix}`;
}
